import React, { useState } from 'react';
import PropTypes from 'prop-types';

import postAddRadioTag from '../../../api/graphql/mutations/postAddRadioTag';
import postEditRadioTag from '../../../api/graphql/mutations/postEditRadioTag';
import postDeleteRadioTag from '../../../api/graphql/mutations/postDeleteRadioTag';
import { LANGS, emptyMultilang, multilangToInput } from '../../../utils/radioShowForm';
import LoadIcon from '../../LoadIcon';
import RadioConfirmModal from './RadioConfirmModal';
import { multilangLabel } from './RadioFields';

/*
Radio tags and categories: one collection, told apart by isCategory. Create (a category
too, which pspanel could not do: its INSERT wrote the column name as the value), rename
in es/ca/en/pt, move between tags and categories, delete.

The list itself is the container's (the show form needs it as well); this view calls
onReload after every change. Empty languages are sent as null, like show titles.
*/

const errorMessages = (result, fallback) => {
  const messages = ((result && result.errors) || []).map(error => error && error.message).filter(Boolean);
  return messages.length ? messages : [fallback];
};

// es, ca and en, as ps-back requires: the website links tags by the English name and hides a
// tag on pages whose language it lacks.
const REQUIRED_NAME_LANGS = ['es', 'ca', 'en'];
const LANG_NAMES = { es: 'Spanish', ca: 'Catalan', en: 'English' };
const missingNameError = (name) => {
  const missing = REQUIRED_NAME_LANGS.filter(lang => !name[lang].trim());
  return missing.length ? `The name is required in ${missing.map(lang => LANG_NAMES[lang]).join(', ')}.` : null;
};

const toNameState = name => LANGS.reduce((acc, lang) => ({ ...acc, [lang]: (name && name[lang]) || '' }), {});

const NameInputs = ({ value, onChange, idPrefix }) => LANGS.map(lang => (
  <label key={lang} htmlFor={`${idPrefix}-${lang}`}>
    {`Name (${lang})${REQUIRED_NAME_LANGS.includes(lang) ? ' *' : ''}`}
    <input
      id={`${idPrefix}-${lang}`}
      type="text"
      value={value[lang]}
      onChange={event => onChange({ ...value, [lang]: event.target.value })}
    />
  </label>
));

NameInputs.propTypes = {
  value: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  idPrefix: PropTypes.string.isRequired,
};

const RadioTagsView = ({
  tags, loading, loadError, onReload, onAuthFail,
}) => {
  const [newName, setNewName] = useState(emptyMultilang());
  const [newIsCategory, setNewIsCategory] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErrors, setCreateErrors] = useState([]);
  const [editing, setEditing] = useState(null); // { id, name, isCategory }
  const [savingEdit, setSavingEdit] = useState(false);
  const [editErrors, setEditErrors] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteErrors, setDeleteErrors] = useState([]);
  const [flash, setFlash] = useState('');

  const handleResult = (result, fallback, setErrors) => {
    if (!result) {
      setErrors(['The server did not answer. Nothing was saved.']);
      return false;
    }
    if (!result.isAuth) {
      onAuthFail();
      return false;
    }
    if (!result.ok) {
      setErrors(errorMessages(result, fallback));
      return false;
    }
    return true;
  };

  const create = async (event) => {
    event.preventDefault();
    const missing = missingNameError(newName);
    if (missing) {
      setCreateErrors([missing]);
      return;
    }
    setCreating(true);
    setCreateErrors([]);
    const result = await postAddRadioTag({ name: multilangToInput(newName), isCategory: newIsCategory });
    setCreating(false);
    if (!handleResult(result, 'It was not created.', setCreateErrors)) return;
    setFlash(`Created ${newIsCategory ? 'category' : 'tag'} “${newName.es}”.`);
    setNewName(emptyMultilang());
    setNewIsCategory(false);
    onReload();
  };

  const saveEdit = async () => {
    const missing = missingNameError(editing.name);
    if (missing) {
      setEditErrors([missing]);
      return;
    }
    setSavingEdit(true);
    setEditErrors([]);
    const result = await postEditRadioTag({ id: editing.id, name: multilangToInput(editing.name), isCategory: editing.isCategory });
    setSavingEdit(false);
    if (!handleResult(result, 'It was not saved.', setEditErrors)) return;
    setFlash(`Saved #${editing.id}.`);
    setEditing(null);
    onReload();
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteErrors([]);
    const result = await postDeleteRadioTag({ id: deleteTarget.id });
    setDeleting(false);
    if (!handleResult(result, 'It was not deleted.', setDeleteErrors)) return;
    setFlash(`Deleted “${multilangLabel(deleteTarget.name, `#${deleteTarget.id}`)}”.`);
    setDeleteTarget(null);
    onReload();
  };

  const renderRow = (tag) => {
    if (editing && editing.id === tag.id) {
      return (
        <tr key={tag.id}>
          <td>{tag.id}</td>
          {LANGS.map(lang => (
            <td key={lang}>
              <input
                type="text"
                aria-label={`Name (${lang})`}
                value={editing.name[lang]}
                onChange={event => setEditing({ ...editing, name: { ...editing.name, [lang]: event.target.value } })}
              />
            </td>
          ))}
          <td>
            <label htmlFor={`radio-tag-${tag.id}-category`} className="flex items-center">
              <input
                id={`radio-tag-${tag.id}-category`}
                type="checkbox"
                checked={editing.isCategory}
                onChange={event => setEditing({ ...editing, isCategory: event.target.checked })}
              />
              <span style={{ marginLeft: 6 }}>Category</span>
            </label>
          </td>
          <td>{tag.usage || 0}</td>
          <td>
            <div className="radio-table-actions">
              <button type="button" className="radio-light-button is-ghost" onClick={() => setEditing(null)} disabled={savingEdit}>Cancel</button>
              <button type="button" className="radio-light-button" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save'}</button>
            </div>
            {editErrors.length ? <p className="radio-error-text">{editErrors.join(' ')}</p> : null}
          </td>
        </tr>
      );
    }
    return (
      <tr key={tag.id}>
        <td>{tag.id}</td>
        {LANGS.map(lang => <td key={lang}>{(tag.name && tag.name[lang]) || <span className="radio-help">—</span>}</td>)}
        <td>{tag.isCategory ? <span className="radio-badge is-category">Category</span> : <span className="radio-badge">Tag</span>}</td>
        <td>{tag.usage || 0}</td>
        <td>
          <div className="radio-table-actions">
            <button
              type="button"
              className="radio-light-button is-ghost"
              onClick={() => {
                setEditErrors([]);
                setEditing({ id: tag.id, name: toNameState(tag.name), isCategory: Boolean(tag.isCategory) });
              }}
            >
              Edit
            </button>
            <button
              type="button"
              className="radio-light-button is-danger"
              onClick={() => {
                setDeleteErrors([]);
                setDeleteTarget(tag);
              }}
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderTable = (title, list) => (
    <>
      <h3 className="radio-subheading font-americaMonoBold">{`${title} (${list.length})`}</h3>
      {list.length ? (
        <table className="radio-tags-table">
          <thead>
            <tr>
              <th>id</th>
              {LANGS.map(lang => <th key={lang}>{lang}</th>)}
              <th>type</th>
              <th title="Non-deleted shows that use it">used by</th>
              <th />
            </tr>
          </thead>
          <tbody>{list.map(renderRow)}</tbody>
        </table>
      ) : <p className="radio-empty">None yet.</p>}
    </>
  );

  const byName = (a, b) => multilangLabel(a.name, '').localeCompare(multilangLabel(b.name, ''), 'es');
  const categories = tags.filter(tag => tag.isCategory).sort(byName);
  const plainTags = tags.filter(tag => !tag.isCategory).sort(byName);

  return (
    <div>
      <div className="flex justify-between mb-4">
        <span className="text-americaMonoBold text-26">Tags & categories</span>
      </div>
      <form className="radio-tag-create" onSubmit={create}>
        <NameInputs value={newName} onChange={setNewName} idPrefix="radio-new-tag" />
        <label className="radio-inline-check" htmlFor="radio-new-tag-category">
          <input id="radio-new-tag-category" type="checkbox" checked={newIsCategory} onChange={event => setNewIsCategory(event.target.checked)} />
          Is category
        </label>
        <button type="submit" className="radio-light-button" disabled={creating}>{creating ? 'Adding…' : 'Add'}</button>
      </form>
      {createErrors.length ? <p className="radio-error-text">{createErrors.join(' ')}</p> : null}
      {flash ? (
        <div className="radio-flash">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash('')}>Dismiss</button>
        </div>
      ) : null}
      {loading ? <LoadIcon /> : null}
      {!loading && loadError ? <p className="radio-error-text">{loadError}</p> : null}
      {!loading && !loadError ? (
        <>
          {renderTable('Categories', categories)}
          {renderTable('Tags', plainTags)}
        </>
      ) : null}
      {deleteTarget ? (
        <RadioConfirmModal
          title={`Delete ${deleteTarget.isCategory ? 'category' : 'tag'} #${deleteTarget.id}`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          busy={deleting}
          errors={deleteErrors}
        >
          <p>{`Delete “${multilangLabel(deleteTarget.name, `#${deleteTarget.id}`)}”?`}</p>
          <p>
            {deleteTarget.usage
              ? `It is used by ${deleteTarget.usage} show(s); they will lose it.`
              : 'No show uses it.'}
          </p>
        </RadioConfirmModal>
      ) : null}
    </div>
  );
};

RadioTagsView.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.shape({}),
    isCategory: PropTypes.bool,
    usage: PropTypes.number,
  })),
  loading: PropTypes.bool,
  loadError: PropTypes.string,
  onReload: PropTypes.func.isRequired,
  onAuthFail: PropTypes.func.isRequired,
};

RadioTagsView.defaultProps = {
  tags: [],
  loading: false,
  loadError: '',
};

export default RadioTagsView;
